import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Search, RefreshCw, Grid, List, TrendingUp, Info } from 'lucide-react';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertDescription } from '../ui/alert';

// Currency configuration - easy to adjust margins per currency
const currencyConfig = {
  'USD': {
    name: 'US Dollar',
    buyMargin: 0.025,         // PLN to subtract from NBP bid
    sellMargin: 0.025,        // PLN to add to NBP ask
    buyDiscountPercent: null, // No percentage discount for buy
    sellMarkupPercent: null   // No percentage markup for sell
  },
  'CAD': {
    name: 'Canadian Dollar',
    buyMargin: 0.025,
    sellMargin: 0.025,
    buyDiscountPercent: null,
    sellMarkupPercent: null
  },
  'EUR': {
    name: 'Euro',
    buyMargin: 0.025,
    sellMargin: 0.025,
    buyDiscountPercent: null,
    sellMarkupPercent: null
  },
  'CHF': {
    name: 'Swiss Franc',
    buyMargin: 0.025,
    sellMargin: 0.025,
    buyDiscountPercent: null,
    sellMarkupPercent: null
  },
  'GBP': {
    name: 'British Pound',
    buyMargin: 0.025,
    sellMargin: 0.025,
    buyDiscountPercent: null,
    sellMarkupPercent: null
  },
  'CZK': {
    name: 'Czech Koruna',
    buyMargin: 0.025,
    sellMargin: 0.025,
    buyDiscountPercent: null,
    sellMarkupPercent: null
  },
  'DKK': {
    name: 'Danish Krone',
    buyMargin: 0.025,
    sellMargin: 0.025,
    buyDiscountPercent: null,
    sellMarkupPercent: null
  },
  'NOK': {
    name: 'Norwegian Krone',
    buyMargin: 0.025,         // Fallback if percentage not used
    sellMargin: 0.025,        // Fallback if percentage not used
    buyDiscountPercent: 20,   // 20% discount from NBP bid rate
    sellMarkupPercent: null   // Use standard PLN margin for sell
  },
  'SEK': {
    name: 'Swedish Krona',
    buyMargin: 0.025,
    sellMargin: 0.025,
    buyDiscountPercent: null,
    sellMarkupPercent: null
  }
};

const targetCurrencies = Object.keys(currencyConfig);
const currencyNames = Object.fromEntries(
  Object.entries(currencyConfig).map(([code, config]) => [code, config.name])
);

export default function LiveRates() {
  const [ratesData, setRatesData] = useState({ rates: [], effectiveDate: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('cards');
  const [lastFetch, setLastFetch] = useState(null);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('https://api.nbp.pl/api/exchangerates/tables/C?format=json');
      
      if (!response.ok) {
        throw new Error('Failed to fetch rates from the server.');
      }
      
      const data = await response.json();

      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Invalid data format received from the API.');
      }

      const ratesTable = data[0];
      
      if (!ratesTable || !Array.isArray(ratesTable.rates)) {
        throw new Error('Rates data is missing in the API response.');
      }
      
      const filteredRates = ratesTable.rates
        .filter(rate => targetCurrencies.includes(rate.code))
        .map(rate => {
          const config = currencyConfig[rate.code];
          
          // Calculate buy rate - use percentage discount if available, otherwise use PLN margin
          let clientBuyRate;
          if (config.buyDiscountPercent) {
            clientBuyRate = rate.bid * (1 - config.buyDiscountPercent / 100);
          } else {
            clientBuyRate = rate.bid - config.buyMargin;
          }
          
          // Calculate sell rate - use percentage markup if available, otherwise use PLN margin
          let clientSellRate;
          if (config.sellMarkupPercent) {
            clientSellRate = rate.ask * (1 + config.sellMarkupPercent / 100);
          } else {
            clientSellRate = rate.ask + config.sellMargin;
          }
          
          return {
            ...rate,
            clientBuyRate,
            clientSellRate
          };
        });
      
      setRatesData({
        rates: filteredRates,
        effectiveDate: ratesTable.effectiveDate
      });
      setLastFetch(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Fetch rates error:", err);
      setError(err.message || 'Rates are temporarily unavailable. Please try again soon.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 300000); // Update every 5 minutes
    return () => clearInterval(interval);
  }, [fetchRates]);

  const filteredAndSearchedRates = ratesData.rates ? ratesData.rates.filter(rate =>
    rate.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (currencyNames[rate.code] && currencyNames[rate.code].toLowerCase().includes(searchTerm.toLowerCase()))
  ) : [];

  const LoadingSkeleton = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array(6).fill(0).map((_, i) => (
        <Card key={i} className="animate-pulse">
          <CardHeader>
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-4 w-32" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-8 w-20" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const ErrorState = () => (
    <div className="text-center py-12">
      <Alert className="max-w-md mx-auto">
        <Info className="w-4 h-4" />
        <AlertDescription className="text-left">
          <p className="mb-4">{error}</p>
          <Button onClick={fetchRates} className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );

  const RateCard = ({ rate }) => (
    <Card className="hover:shadow-xl transition-all duration-300 border-l-4 border-l-green-600 group bg-white">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <Badge className="bg-blue-900 text-white font-bold text-sm px-3 py-1">
            {rate.code}
          </Badge>
          <TrendingUp className="w-4 h-4 text-green-600 group-hover:scale-110 transition-transform" />
        </div>
        <h3 className="font-semibold text-blue-900">{currencyNames[rate.code]} / Polish Zloty</h3>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-sm font-medium text-green-700 mb-1">We Buy</p>
            <p className="text-xl font-bold text-green-600">{rate.clientBuyRate.toFixed(4)}</p>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <p className="text-sm font-medium text-red-700 mb-1">We Sell</p>
            <p className="text-xl font-bold text-red-600">{rate.clientSellRate.toFixed(4)}</p>
          </div>
        </div>
        <div className="text-center text-xs text-gray-500 border-t pt-2">
          <p>Last refresh: {lastFetch || 'Loading...'}</p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <section id="rates" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">
            Live Exchange Rates
          </h2>
          <div className="w-24 h-1 bg-green-600 mx-auto mb-6"></div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-4">
            Real-time currency exchange rates with our competitive margins
          </p>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search currency..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'cards' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className="flex items-center gap-2"
            >
              <Grid className="w-4 h-4" />
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('table')}
              className="flex items-center gap-2"
            >
              <List className="w-4 h-4" />
              Table
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <ErrorState />
        ) : viewMode === 'cards' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSearchedRates.map((rate) => (
              <RateCard key={rate.code} rate={rate} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Currency</th>
                    <th className="px-6 py-4 text-center">We Buy (PLN)</th>
                    <th className="px-6 py-4 text-center">We Sell (PLN)</th>
                    <th className="px-6 py-4 text-center">NBP Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSearchedRates.map((rate, index) => (
                    <tr key={rate.code} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <Badge className="bg-blue-900 text-white">{rate.code}</Badge>
                          <span className="font-medium">{currencyNames[rate.code]}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-bold text-green-600 text-lg">{rate.clientBuyRate.toFixed(4)}</span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="font-bold text-red-600 text-lg">{rate.clientSellRate.toFixed(4)}</span>
                      </td>
                      <td className="px-6 py-4 text-center text-sm text-gray-600">
                        {rate.bid.toFixed(4)} - {rate.ask.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}